import React, { Suspense, useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import ListSubheader from '@mui/material/ListSubheader';
import Menu from '@mui/material/Menu';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { ExpandMore, Search, Language as LanguageIcon } from '@mui/icons-material';
import { SUPPORTED_LANGUAGES, localizePath, setLanguageIntent, stripLanguagePrefix } from '../i18n/languages';
import { trackPageView } from '../utils/analytics';

const NAVIGATION_GROUPS = [
  {
    key: 'data',
    items: [
      { key: 'catalog', path: '/' },
      { key: 'map', path: '/map' },
      { key: 'pulse', path: '/pulse' },
    ],
  },
  {
    key: 'analysis',
    items: [
      { key: 'compare', path: '/compare' },
      { key: 'forecastAccuracy', path: '/forecast-accuracy' },
      { key: 'studio', path: '/studio/US' },
    ],
  },
  {
    key: 'updates',
    items: [
      { key: 'releaseCalendar', path: '/calendar' },
      { key: 'briefing', path: '/briefing' },
    ],
  },
  {
    key: 'learning',
    items: [
      { key: 'guides', path: '/guides/understanding-gdp' },
      { key: 'blog', path: '/blog' },
    ],
  },
];

const isRouteActive = (currentPath, route) =>
  currentPath === route || (route !== '/' && currentPath.startsWith(`${route}/`));

// Native names shown in the language selector so users can find their
// language regardless of the currently active one.
const NATIVE_LANGUAGE_NAMES = {
  en: 'English',
  ua: 'Українська',
  es: 'Español',
  de: 'Deutsch',
  fr: 'Français',
  'zh-CN': '中文',
  ja: '日本語',
};

interface LayoutProps {
  children: React.ReactNode;
}

function Layout(props: LayoutProps) {
  const { t, i18n } = useTranslation(['translation']);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchVal, setSearchVal] = useState('');
  const [navigationAnchor, setNavigationAnchor] = useState(null);
  const currentPath = stripLanguagePrefix(location.pathname).replace(/\/+$/, '') || '/';
  const isNavigationActive = NAVIGATION_GROUPS.some((group) =>
    group.items.some((item) => isRouteActive(currentPath, item.path))
  );
  const navigationOpen = Boolean(navigationAnchor);

  // initialize from URL query
  useEffect(() => {
    const q = new URLSearchParams(location.search).get('q') || '';
    setSearchVal(q);
  }, [location.search]);

  // Report every SPA navigation to GA4 (consent-gated). index.html
  // suppresses the automatic initial page view, so this is the single
  // source of page_view hits.
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);

  const changeLanguage = (lng) => {
    // Navigating to the localized URL is what actually switches the language;
    // the intent flag prevents the URL-sync effect from treating an
    // unprefixed (English) URL as "keep the previous language".
    setLanguageIntent(lng);
    navigate(localizePath(location.pathname, lng));
  };

  // debounce navigation to avoid frequent route updates while typing
  const searchDebounceRef = React.useRef();
  const handleSearch = (e) => {
    e.preventDefault();
  };

  // cleanup debounce on unmount
  React.useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  return (
    <Suspense fallback={<div className="my-spinner">{t('common.loading')}</div>}>
      <a href="#main-content" className="skip-link">{t('common.skipToMainContent')}</a>
      <header className="wecobook-header" style={{ position: 'sticky', top: 0, zIndex: 1000, padding: '12px 0' }}>
        <Container maxWidth="xl">
          <div className="wecobook-header-row">
            {/* Logo */}
            <div className="wecobook-brand" onClick={() => navigate('/')}>
              <span className="brand-logo-w">W</span>
              <span>WecoBook</span>
            </div>

            {/* Search Bar */}
            <form onSubmit={handleSearch} className="wecobook-search">
              <div className="search-container">
                <Search style={{ color: '#64748b', fontSize: 20 }} />
                <input
                  type="text"
                  placeholder={t('nav.searchPlaceholder')}
                  value={searchVal}
                  onChange={(e) => {
                    const v = e.target.value;
                    setSearchVal(v);
                    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
                    searchDebounceRef.current = setTimeout(() => {
                      const q = v ? `?q=${encodeURIComponent(v)}` : '';
                      navigate(`/${q}`, { replace: true });
                    }, 350);
                  }}
                  aria-label={t('nav.searchAria')}
                />
              </div>
            </form>

            {/* Nav & Language Switch */}
            <div className="wecobook-header-actions">
              <Button
                id="explore-menu-button"
                className={`nav-tab-btn nav-explore-trigger ${isNavigationActive ? 'active' : ''}`}
                aria-controls={navigationOpen ? 'explore-menu' : undefined}
                aria-expanded={navigationOpen}
                aria-haspopup="menu"
                onClick={(event) => setNavigationAnchor(event.currentTarget)}
                endIcon={<ExpandMore className={navigationOpen ? 'nav-explore-chevron is-open' : 'nav-explore-chevron'} />}
              >
                {t('nav.explore')}
              </Button>
              <Menu
                id="explore-menu"
                anchorEl={navigationAnchor}
                open={navigationOpen}
                onClose={() => setNavigationAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{ className: 'nav-explore-menu-paper' }}
                MenuListProps={{ 'aria-labelledby': 'explore-menu-button', autoFocusItem: true }}
              >
                {NAVIGATION_GROUPS.flatMap((group, groupIndex) => [
                  ...(groupIndex > 0
                    ? [<Divider key={`divider-${group.key}`} component="li" className="nav-explore-divider" />]
                    : []),
                  <ListSubheader key={`heading-${group.key}`} className="nav-explore-group-heading">
                    {t(`nav.groups.${group.key}`)}
                  </ListSubheader>,
                  ...group.items.map((item) => (
                    <MenuItem
                      key={item.key}
                      component={Link}
                      to={localizePath(item.path, i18n.language)}
                      aria-label={`${t(`nav.groups.${group.key}`)}: ${t(`nav.${item.key}`)}`}
                      selected={isRouteActive(currentPath, item.path)}
                      onClick={() => setNavigationAnchor(null)}
                      className="nav-explore-item"
                    >
                      {t(`nav.${item.key}`)}
                    </MenuItem>
                  )),
                ])}
              </Menu>

              <Select
                value={SUPPORTED_LANGUAGES.includes(i18n.language) ? i18n.language : 'en'}
                onChange={(e) => changeLanguage(e.target.value)}
                size="small"
                aria-label={t('nav.selectLanguage')}
                renderValue={(selected) => (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px' }}>
                    <LanguageIcon style={{ fontSize: 16, color: 'var(--text-secondary)' }} />
                    {selected.toUpperCase()}
                  </span>
                )}
                sx={{
                  minWidth: 80,
                  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { border: 'none' },
                  '& .MuiSelect-select': { padding: '4px 8px', paddingRight: '24px !important' },
                  '& .MuiSelect-icon': { color: 'var(--text-secondary)' },
                }}
              >
                {SUPPORTED_LANGUAGES.map((lng) => (
                  <MenuItem key={lng} value={lng} sx={{ fontSize: '14px' }}>
                    <span style={{ fontWeight: i18n.language === lng ? 600 : 400 }}>
                      {NATIVE_LANGUAGE_NAMES[lng]}
                    </span>
                    <span style={{ color: 'var(--text-secondary)', marginLeft: 'auto', paddingLeft: '12px', fontSize: '12px' }}>
                      {lng}
                    </span>
                  </MenuItem>
                ))}
              </Select>
            </div>
          </div>
        </Container>
      </header>

      {/* Main Body */}
      <main id="main-content" style={{ minHeight: 'calc(100vh - 180px)', padding: '24px 0' }}>
        <Container maxWidth="xl">
          {props.children}
        </Container>
      </main>

      {/* Footer */}
      <footer className="wecobook-footer">
        <Container maxWidth="xl">
          <p>{t('footer.rights', { year: new Date().getFullYear() })}</p>
        </Container>
      </footer>
    </Suspense>
  );
}

export default Layout;
